import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Star, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'ptg_review_prompt';
const COOLDOWN_MS = 30 * 24 * 3600 * 1000;

export function ReviewPopup() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const last = parseInt(localStorage.getItem(KEY) || '0', 10);
        if (Date.now() - last < COOLDOWN_MS) return;
        const timer = setTimeout(() => setOpen(true), 8000);
        return () => clearTimeout(timer);
    }, []);

    const dismiss = () => {
        localStorage.setItem(KEY, String(Date.now()));
        setOpen(false);
    };

    const submit = async () => {
        if (!rating || text.trim().length < 10) {
            toast.error(t('reviewPopup.validation'));
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/reviews`, { rating, text: text.trim() }, { withCredentials: true });
            toast.success(t('reviewPopup.thanks'));
            dismiss();
        } catch (e) {
            if (e.response?.status === 429) {
                toast.info(e.response.data.detail);
                dismiss();
            } else {
                toast.error('Failed to submit review');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
            <DialogContent className="max-w-md" data-testid="review-popup">
                <DialogHeader>
                    <DialogTitle className="text-center text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t('reviewPopup.title')}
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        {t('reviewPopup.subtitle')}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-center gap-2 py-2" data-testid="review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} data-testid={`review-star-${n}`}>
                            <Star className="w-8 h-8 transition-transform hover:scale-110" style={{ color: '#FFB800', fill: (hover || rating) >= n ? '#FFB800' : 'transparent' }} />
                        </button>
                    ))}
                </div>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={600} placeholder={t('reviewPopup.placeholder')} data-testid="review-text-input" />
                <div className="flex flex-col gap-2">
                    <Button className="btn-primary w-full" onClick={submit} disabled={submitting} data-testid="review-submit-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('reviewPopup.submit')}
                    </Button>
                    <Button variant="ghost" className="w-full" onClick={dismiss} data-testid="review-later-btn">
                        {t('reviewPopup.later')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
